import React from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { CALIBRATE_ANIM } from '../../../../theme/tokens';
import { BusinessValueWaves } from '../BusinessValue';
import { BokehField } from './Bokeh';
import { FocusIcon } from './FocusIcon';
import { useTypewriter } from './useTypewriter';
import {
  Panel,
  Waves,
  Centerer,
  Content,
  IconWrap,
  Heading,
  Typed,
  Caret,
  Caption,
  DaysChip,
  SrOnly,
} from './PulseCalibrating.styles';

export type PulseCalibratingProps = {
  /** Whole days left in the launch calibration window. 0 when the brand is past the window
   *  (i.e. the calibrating state is driven by "no revenue yet" rather than the countdown),
   *  in which case the day-count chip is hidden. */
  daysLeft: number;
};

/**
 * The Pulse "Calibrating" panel. One big rounded card pinned over the whole Pulse content
 * column, covering every section card, shown for a brand-new brand (under 5 days old) or one
 * with no revenue yet, where the figures are not yet meaningful (see {@link usePulseData}). It
 * frosts the dashboard beneath; focus-synced bokeh particles drift inside it (background and
 * foreground, clipped to the panel); and it carries the same glow waves as the Business Value
 * hero, dialled 50% more vivid.
 *
 * The centred content pairs an animated autofocus mark (a camera lens hunting for focus, the
 * icon's ring racking sharp) with a typewriter that cycles one status line per covered section,
 * plus a countdown chip telling the owner how many days remain before their data appears.
 * Honours reduced motion (the typewriter cross-fades whole phrases; the ring, bokeh and halo
 * hold still).
 */
export const PulseCalibrating = ({ daysLeft }: PulseCalibratingProps): React.ReactElement => {
  const { t } = useTranslation();
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');

  // One line per Pulse section, in page order. Literal `t()` calls so the i18n extractor
  // harvests them and a language switch re-renders the list.
  const phrases = React.useMemo(
    () => [
      t('Calibrating your business value'),
      t("Reading this month's signals"),
      t('Gathering player base insights'),
      t('Benchmarking against the network'),
      t('Identifying market tailwinds'),
    ],
    [t],
  );

  const { text, index, busy } = useTypewriter(phrases, {
    typeMs: CALIBRATE_ANIM.typeMs,
    deleteMs: CALIBRATE_ANIM.deleteMs,
    holdMs: CALIBRATE_ANIM.holdMs,
    gapMs: CALIBRATE_ANIM.gapMs,
    reduced,
  });

  // Countdown copy (singular vs plural via two literal keys, so the extractor harvests both
  // and we avoid i18next plural machinery). The resolved string is handed to the chip.
  const daysCopy =
    daysLeft === 1
      ? t('About 1 day until your Pulse page is available')
      : t('About {{days}} days until your Pulse page is available', { days: daysLeft });

  return (
    <Panel role="status">
      <Waves>
        <BusinessValueWaves intensity={1.5} stars={false} />
      </Waves>
      <BokehField layer="bg" z={1} />
      <Centerer>
        <Content>
          <IconWrap>
            <FocusIcon />
          </IconWrap>
          <Heading>
            <Typed key={index} aria-hidden="true">
              {text}
              <Caret $busy={busy} />
            </Typed>
            <SrOnly text="Calibrating your Pulse page" tag="span" />
          </Heading>
          <Caption text="Your Pulse page comes into focus as new data arrives." tag="span" />
          {daysLeft > 0 ? <DaysChip text={daysCopy} tag="span" /> : null}
        </Content>
      </Centerer>
      <BokehField layer="fg" z={3} />
    </Panel>
  );
};

export default PulseCalibrating;
